import React, { useContext, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { HistoryManagerContext } from 'components/hooks/HistoryManagerProvider'
import { SET_SERVICE_DELIVERY_TYPE } from 'constants/actionTypes'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'

import { GiHamburgerMenu } from 'react-icons/gi'
import { AiFillHome } from 'react-icons/ai'

import {
  AvatarIcon,
  CloseIcon,
  Logo,
  LogoutIcon,
  TickIcon,
  TrackingIcon
} from 'components/common'
import { Profile } from 'components'
import s from './Header.module.css'
import ServiceDeliveryOptions from 'components/ServiceList/ServiceDeliveryOptions'
import { ModalRef } from 'components/common/Modal/Modal'
import { DeliveryType } from 'components/common/types'
import {
  useAppDispatch,
  useAppSelector
} from 'components/hooks/redux-selectors'
import SelectDeliveryAreaModal from 'components/SelectDeliveryAreaModal/SelectDeliveryAreaModal'
import SelectServiceTimeModal from 'components/SelectServiceTimeModal/SelectServiceTimeModal'

const Header = () => {
  const { isAuthenticated, data } = useAppSelector((state) => state.auth)
  const { businessListData } = useAppSelector((state) => state.serviceList)
  const { data: regions, isLoading: isRegionsLoading } = useAppSelector(
    (state) => state.homepage.regions
  )
  const { selectedRegion } = useAppSelector((state) => state.serviceList)
  const router = useRouter()
  const regionQuery = (router?.query?.index as string[])?.[0]
  const regionId = regionQuery ? regionQuery : undefined

  const history = useContext(HistoryManagerContext)

  const selectDeliveryAreaModalRef = useRef<ModalRef>(null)
  const selectServicetimeModalRef = useRef<ModalRef>(null)
  const [isOpen, setIsOpen] = React.useState(false)

  const showDeliveryOptions = router.pathname.includes('/services')

  useLockBodyScroll(isOpen)

  const toggleDrawer = () => {
    setIsOpen((prevState) => !prevState)
  }

  const dispatch = useAppDispatch()

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT_SUCCESS' })
    router.push('/')
    toggleDrawer()
  }

  const isPrivate =
    router.query.isPrivate === '1' ||
    history?.[history.length - 2]?.includes('isPrivate=1')

  return (
    <header className={s.nav}>
      <div className="flex justify-between w-full xl:w-auto items-center">
        {isPrivate ? (
          <>
            <div>
              <AiFillHome className="w-[30px] h-[30px] fillg-gray-800 xl:hidden" />
            </div>
            <Logo className="w-[166px] h-[61px] hidden xl:block" />
          </>
        ) : (
          <Link href="/">
            <a>
              <div>
                <AiFillHome className="w-[30px] h-[30px] fill-gray-800 xl:hidden" />
              </div>
              <Logo className="w-[166px] h-[61px] hidden xl:block" />
            </a>
          </Link>
        )}

        <div className="text-3xl xl:hidden font-bold">
          {selectedRegion.name}
        </div>

        <div className="xl:hidden">
          <GiHamburgerMenu
            className="w-[28px] h-[28px]"
            onClick={toggleDrawer}
          />
        </div>
      </div>

      {/* {checkIsSpecialBusiness(businessListData?.business_type_id) && (
        <ScrollY>
          {(pageOffset) => (
            <ScrollChecker
              pageOffset={pageOffset}
              elementId="service-delivery-options"
              toggleClass={() => {
                if (
                  selectedRegion.services.length &&
                  checkIsSpecialBusiness(businessListData.business_type_id)
                ) {
                  return s.stickyServiceDeliveryOptionsBottom
                }

                if (
                  !selectedRegion.services.length &&
                  checkIsSpecialBusiness(businessListData.business_type_id)
                ) {
                  return s.stickyServiceDeliveryOptionsTop
                }

                return []
              }}
              shouldToggleOn={pageOffset > 200}
            > */}

      {showDeliveryOptions && (
        <ServiceDeliveryOptions
          businessTypeId={businessListData?.business_type_id}
          onAddressClick={() => {
            selectDeliveryAreaModalRef.current?.openModal()
          }}
          onDeliveryTypeClick={(deliveryType) => {
            if (deliveryType === DeliveryType.DELIVER) {
              selectDeliveryAreaModalRef.current?.openModal()
            }

            if (deliveryType === DeliveryType.PICK_UP) {
              dispatch({
                type: SET_SERVICE_DELIVERY_TYPE,
                payload: deliveryType
              })
            }
          }}
          onServiceTimeClick={() => {
            selectServicetimeModalRef.current?.openModal()
          }}
        />
      )}

      {/* </ScrollChecker>
          )}
         </ScrollY>
      )} */}
      <Drawer
        open={isOpen}
        onClose={toggleDrawer}
        direction="right"
        style={{
          zIndex: 9999
        }}
        className="overflow-y-auto relative !z-[9999]"
      >
        <DrawerContent
          isRegionsLoading={isRegionsLoading}
          toggleDrawer={toggleDrawer}
          isAuthenticated={isAuthenticated}
          onCloseClick={toggleDrawer}
          regions={regions}
          currentRegionSlug={regionId}
          onLogoutClick={handleLogout}
          userName={data.UserName}
        />
      </Drawer>

      <SelectDeliveryAreaModal
        modalRef={selectDeliveryAreaModalRef}
        isoCode={businessListData?.country_iso_code}
      />

      <SelectServiceTimeModal modalRef={selectServicetimeModalRef} />

      <div className="hidden xl:block">
        {isAuthenticated ? (
          <Profile onLogoutClick={handleLogout} />
        ) : (
          <>
            <div className={s.linkWrapper}>
              <Link href="/login">
                <a className={s.signIn}>Sign In</a>
              </Link>
              <div className={s.signUpWrapper}>
                <Link href="/register">
                  <a className={s.signUp}>Sign Up</a>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  )
}

const DrawerContent = ({
  regions,
  onCloseClick,
  toggleDrawer,
  isAuthenticated,
  onLogoutClick,
  userName,
  currentRegionSlug,
  isRegionsLoading
}) => {
  return (
    <div className="flex flex-col p-4 overflow-y-auto pb-40">
      <div
        onClick={onCloseClick}
        className="flex items-center ml-auto cursor-pointer max-w-max  rounded-md p-2 bg-black/60 md:right-2"
      >
        <CloseIcon className="h-8 w-8 fill-white" />
      </div>

      {isAuthenticated ? (
        <div className="bg-gray-50 p-4 mb-4 mt-2 rounded-md border border-solid border-gray-600">
          <div className="text-3xl font-bold p-2">Account</div>

          <div className="flex flex-col gap-y-1">
            <div className="text-2xl text-black p-2">{userName}</div>

            <Link href="/orders">
              <a className="text-black no-underline text-[1.6rem] p-2">
                <div className="flex items-center">
                  <div className="w-[40px]">
                    <TrackingIcon className="min-w-[28px] min-h-[30px]" />
                  </div>
                  <span>Orders</span>
                </div>
              </a>
            </Link>

            <Link href="/accountSettings">
              <a className="text-black no-underline text-[1.6rem] p-2">
                <div className="flex items-center">
                  <div className="w-[40px]">
                    <AvatarIcon className="min-w-[30px] min-h-[22px] ml-[-2px]" />
                  </div>
                  <span>Account Settings</span>
                </div>
              </a>
            </Link>

            <div
              className="p-2 flex items-center cursor-pointer"
              onClick={onLogoutClick}
            >
              <div className="w-[40px]">
                <LogoutIcon className="min-w-[25px] min-h-[18px]" />
              </div>
              <span>Logout</span>
            </div>
          </div>
        </div>
      ) : (
        <div className={`${s.linkWrapper} mt-12 mb-8 flex justify-between`}>
          <Link href="/login">
            <a className={s.signIn} onClick={toggleDrawer}>
              Sign In
            </a>
          </Link>
          <div className={s.signUpWrapper}>
            <Link href="/register">
              <a className={s.signUp} onClick={toggleDrawer}>
                Sign Up
              </a>
            </Link>
          </div>
        </div>
      )}

      <div className="bg-gray-50 p-4 border border-solid border-gray-600 rounded-md">
        <div className="text-3xl font-bold p-2 pl-1">Shopping locations</div>

        <div className="flex flex-col gap-y-1">
          {isRegionsLoading && (
            <>
              <div className="skeleton w-full h-12 mb-4"></div>
              <div className="skeleton w-full h-12 mb-4"></div>
              <div className="skeleton w-full h-12 mb-4"></div>
              <div className="skeleton w-full h-12 mb-4"></div>
            </>
          )}

          {regions.map((region) => {
            const url = region?.services?.length
              ? `/services/${region.slug}/${region.services[0].slug}`
              : `/services/${region.slug}`

            const isActive = region.slug === currentRegionSlug

            return (
              <Link href={url}>
                <a
                  onClick={toggleDrawer}
                  className={`text-[1.6rem] p-2  no-underline flex items-center ${
                    isActive ? 'text-green' : 'text-black'
                  }`}
                >
                  {region.name}

                  {isActive && (
                    <TickIcon className="min-w-[18px] min-h-[18px] ml-4" />
                  )}
                </a>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Header

const useLockBodyScroll = (shouldLock) => {
  useEffect(() => {
    if (shouldLock) {
      // Lock the body scroll
      document.body.style.overflow = 'hidden'

      // Clean up function to unlock the body scroll when the component unmounts
      return () => {
        document.body.style.overflow = 'unset'
      }
    }
  }, [shouldLock])
}
